import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { locations } from '@/locations';
import { FormattedText } from '@/components/uiKit/FormattedText';
import { useSendCodeMutation, useValidateCodeMutation } from '@/store/api/authApi';
import { OTP_COUNTER_INITIAL_TIME, TOAST_STATUS } from '@/const';
import Counter from '@/pages/auth/components/Counter';
import { useLazyGetUsersQuery } from '@/store/api/userApi';
import { analytics } from '@/analytics';
import { useAuth } from '@/providers/AuthProvider';
import * as Sentry from '@sentry/react';
import { useToast } from '@/hooks/shared/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Simple PIN Input Component
const PinInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [digits, setDigits] = useState<string[]>(value ? value.split('') : ['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, newValue: string) => {
    // Only allow single digit
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }
    
    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);
    onChange(newDigits.join(''));

    // Move to next input if value entered
    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newDigits = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setDigits(newDigits);
      onChange(newDigits.join(''));
      // Focus on the next empty field or last field
      const nextEmptyIndex = newDigits.findIndex(d => !d);
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  useEffect(() => {
    if (value === '') {
      setDigits(['', '', '', '', '', '']);
    }
  }, [value]);

  return (
    <div className="flex gap-4">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          className="w-12 h-12 text-center text-lg font-semibold border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="*"
        />
      ))}
    </div>
  );
};

export const CodeVerificationForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const counterRef = useRef<{ resetTimer: () => void }>(null);
  const [searchParams] = useSearchParams();
  const [validateCode, { isLoading: isValidationCode }] = useValidateCodeMutation();
  const [fetchUsers, { isLoading: isLoadingUsers }] = useLazyGetUsersQuery();
  const [sendVerificationCode, { isLoading: isSendingCode }] = useSendCodeMutation();
  const [codeValue, setCodeValue] = useState<string>('');
  const [selectedMethod, setSelectedMethod] = useState<string>('email');
  const [errorMessage, setErrorMessage] = useState<string>();
  const [errorStatus, setErrorStatus] = useState<string>();
  const [resendWasClicked, setResendWasClicked] = useState(false);

  const handleCounterReset = () => {
    if (counterRef.current) {
      counterRef.current.resetTimer();
    }
  };

  const onSubmit = async () => {
    if (codeValue?.length === 6) {
      validateCode({
        method: selectedMethod,
        code: codeValue,
      })
        .unwrap()
        .then(async () => {
          toast({
            title: 'OTP Verified Successfully',
            description: 'Your verification code has been successfully verified. You can proceed with the next steps.',
          });

          if (user) {
            await fetchUsers({ uids: user.uid })
              .unwrap()
              .then(async (data) => {
                const userData = data[0];
                if (userData) {
                  analytics.identify(userData?.email, {
                    name: userData?.displayName,
                    firstName: userData?.first_name,
                    lastName: userData?.last_name,
                    email: userData?.email,
                    id: userData?.user_id,
                    role: userData?.role,
                  });
                }
                if (localStorage.getItem('afterMfaNavigateToPasswordResetPage')) {
                  localStorage.removeItem('afterMfaNavigateToPasswordResetPage');
                  localStorage.setItem('userMustChangePassword', 'true');
                  navigate(locations.public.change);
                } else {
                  navigate(locations.protected.sites.index);
                }
              });
          }
        })
        .catch((e) => {
          if (e?.data?.msg) {
            Sentry.captureException(e);
            setErrorMessage(e?.data?.msg);
          }
          if (e?.data?.verification_status) {
            Sentry.captureException(e);
            setErrorStatus(e?.data?.verification_status);
          }
        });
    }
  };

  const onResend = async () => {
    setResendWasClicked(true);
    await sendVerificationCode({ method: selectedMethod }).unwrap();
    handleCounterReset();

    setErrorStatus(undefined);
    setErrorMessage(undefined);

    setCodeValue('');

    toast({
      title: 'Your OTP code has been sent!',
      description: 'You\'re just one step away from completing the process.',
    });
  };

  const onChange = (value: string) => {
    setCodeValue(value);
  };

  useEffect(() => {
    setSelectedMethod(searchParams.get('method') as string);
  }, []);

  const isLoading = isValidationCode || isLoadingUsers;

  return (
    <div>
      <div className="flex flex-col space-y-8">
        <div className="pb-2">
          <PinInput value={codeValue} onChange={onChange} />
        </div>
      </div>

      {errorStatus === 'expired' ? null : <Counter ref={counterRef} initialTime={OTP_COUNTER_INITIAL_TIME} />}

      {errorMessage ? (
        <div className="w-full text-center pb-0 pt-0 text-sm">
          <div className="text-red-600">{errorMessage}</div>
        </div>
      ) : null}

      <div className="flex flex-col-reverse md:flex-row mt-8 justify-between gap-4">
        <div className="w-full text-center">
          <Button 
            variant="ghost" 
            onClick={onResend} 
            disabled={isSendingCode || resendWasClicked}
            className="text-blue-600 hover:text-blue-700"
          >
            {isSendingCode ? 'Sending...' : <FormattedText label="auth.2fa.resend" />}
          </Button>
        </div>

        <div className="w-full text-center mb-5 md:mb-0">
          <Button
            className="w-4/5 md:w-full mx-auto"
            type="submit"
            onClick={onSubmit}
            disabled={!codeValue || codeValue.length < 6 || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : (
              <FormattedText label="auth.2fa.verify" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};